import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import useU2000AxiosAuthenticated from '../../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import {
  doUpdateChildLotChainFromGraphData,
  luHandleAppiError,
  type U3141ChildLotChain,
} from '../../../../U3141Service';
import { u3141MapChildLotChainToGraphUpdatePayload } from '../../../../tools/u3141MapChildLotChainToGraphUpdatePayload';
import { NS } from '../../../../U3141';
import {
  u3141ApplyConcordanceSnapshotForGraphEdit,
  type U3141GraphEditConcordanceSnapshot,
} from '../../../../tools/u3141GraphEditConcordanceSnapshot';

export interface U3141UpdateChildLotFromGraphParams {
  masterLotChainId: string;
  childLotChain: U3141ChildLotChain;
  concordanceSnapshot: U3141GraphEditConcordanceSnapshot;
}

export interface U3141UseUpdateChildLotChainFromGraphHandlersParams {
  t: TFunction;
  toast: { success: (text: string, title?: string) => void; error: (text: string, title?: string) => void };
  onChildUpdated: (child: U3141ChildLotChain) => void;
  patchGraphNode: (child: U3141ChildLotChain) => void;
  refetchMasterLotChain?: () => Promise<void>;
}

export function useU3141UpdateChildLotChainFromGraphHandlers(
  params: U3141UseUpdateChildLotChainFromGraphHandlersParams,
) {
  const { t, toast, onChildUpdated, patchGraphNode, refetchMasterLotChain } = params;
  const axios = useU2000AxiosAuthenticated();

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
      return errorResult;
    },
    [t, toast],
  );

  const updateFromGraph = useCallback(
    async (input: U3141UpdateChildLotFromGraphParams): Promise<boolean> => {
      const { masterLotChainId, childLotChain, concordanceSnapshot } = input;
      const merged = u3141ApplyConcordanceSnapshotForGraphEdit(childLotChain, concordanceSnapshot);
      const payload = u3141MapChildLotChainToGraphUpdatePayload(merged);

      try {
        const response = await doUpdateChildLotChainFromGraphData(axios, masterLotChainId, payload.id, payload);
        const updated = (response.data?.result ?? response.data) as U3141ChildLotChain;
        if (!updated?.id) {
          toast.error(t('errEditLotFromGraphSaveFailed', { ns: NS }), t('errorTitle', { ns: NS }));
          return false;
        }
        onChildUpdated(updated);
        patchGraphNode(updated);
        await refetchMasterLotChain?.();
        toast.success(t('msgEditLotFromGraphSaved', { ns: NS }), t('successTitle', { ns: NS }));
        return true;
      } catch (error) {
        const errorResult = handleApiError(error);
        if (
          !errorResult.genericErrors?.length &&
          errorResult.fieldErrors &&
          Object.keys(errorResult.fieldErrors).length > 0
        ) {
          toast.error(t('errEditLotFromGraphValidation', { ns: NS }), t('errorTitle', { ns: NS }));
        }
        return false;
      }
    },
    [axios, t, toast, onChildUpdated, patchGraphNode, refetchMasterLotChain, handleApiError],
  );

  return { updateFromGraph };
}
